import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"

import Link from "./Link"
import { media, mixins } from "../styles"
import { slugify } from "../helpers"

import AmazonMusicSVG from "../images/icons/amazon-music.inline.svg"
import ApplePodcastsSVG from "../images/icons/apple-podcasts.inline.svg"
import GooglePodcastsSVG from "../images/icons/google-podcasts.inline.svg"
import SpotifySVG from "../images/icons/spotify.inline.svg"

const PlayerWrapper = styled.div`
  background: ${({ theme }) => theme.greyLight};
  color: ${({ theme }) => theme.spaceblue};
  display: block;
  margin: 0 0 30px;
  padding: 35px 20px 15px;
  text-decoration: none;

  > span {
    display: flex;
    flex-direction: column;
  }

  iframe {
    border: none;
    display: block;
    height: 152px;
    width: 100%;

    @media ${media.xl} {
      height: 126px;
    }
  }
`

const Header = styled.div`
  align-items: baseline;
  display: flex;
  flex-flow: row wrap;
  gap: 10px;
  margin: 0 0 20px;
`

const Date = styled.span`
  ${mixins.copy6}
  color: ${({ theme }) => theme.sage};
  margin-left: auto;
`

const Title = styled.h3`
  ${mixins.title6}
  margin: 0;

  a {
    color: ${({ theme }) => theme.spaceblue};
    text-decoration: none;
    transition: color ${({ theme }) => theme.transitionTime};

    &:hover {
      color: ${({ theme }) => theme.sun};
    }
  }
`
const EpisodeMeta = styled.div`
  align-items: center;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  gap: 20px;
  margin: 15px 0 0;

  a {
    color: ${({ theme }) => theme.spaceblue};
    display: block;
    text-decoration: none;
    transition: color ${({ theme }) => theme.transitionTime};

    &:hover {
      color: ${({ theme }) => theme.sun};
    }
  }
`

const EpisodeInfo = styled(Link).attrs({ arrow: true })`
  color: ${({ theme }) => theme.sun};
  margin-right: auto;
  font-weight: 700;

  .link-icon {
    margin-left: 5px;
    vertical-align: -1px;
  }
`

// Unfortunately, the podcasts don't provide captions so we have to turn off this rule:
/* eslint-disable jsx-a11y/media-has-caption */

const PodcastPlayer = ({ date, link, showInfoLink, showTitle, title }) => {
  return (
    <PlayerWrapper>
      <Header>
        {showTitle ? (
          <Title>
            <Link to={`/podcast/${slugify(title)}/`}>{title}</Link>
          </Title>
        ) : null}
        <Date>{date}</Date>
      </Header>
      <iframe
        allowtransparency="true"
        allow="encrypted-media"
        // allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        className="podcast-player-iframe"
        loading="lazy"
        src={`${link.replace(
          "rss.com/podcasts",
          "player.rss.com"
        )}?theme=light`}
        title={title}
        height="140"
        width="100%"
      ></iframe>
      <EpisodeMeta>
        {showInfoLink ? (
          <EpisodeInfo to={`/podcast/${slugify(title)}/`}>Details</EpisodeInfo>
        ) : null}
        <Link
          title="Spotify"
          to="https://open.spotify.com/show/41jx9QDSEwtctm2uPtC8WX"
        >
          <SpotifySVG />
        </Link>
        <Link
          title="Apple Podcasts"
          to="https://podcasts.apple.com/us/podcast/unbundling-embedded-finance/id1685876283"
        >
          <ApplePodcastsSVG />
        </Link>
        <Link
          to="https://music.amazon.com/podcasts/30b57cce-1733-4195-b740-f96e3cb919ac"
          title="Amazon Music"
        >
          <AmazonMusicSVG />
        </Link>
        <Link
          to="https://podcasts.google.com/feed/aHR0cHM6Ly9tZWRpYS5yc3MuY29tL3VuYnVuZGxpbmctZW1iZWRkZWQtZmluYW5jZS9mZWVkLnhtbA"
          title="Google Podcasts"
        >
          <GooglePodcastsSVG />
        </Link>
      </EpisodeMeta>
    </PlayerWrapper>
  )
}

PodcastPlayer.propTypes = {
  date: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  showInfoLink: PropTypes.bool,
  showTitle: PropTypes.bool,
  title: PropTypes.string.isRequired,
}

export default PodcastPlayer
